// Pure utility functions extracted from openai.js
// These are stateless, side-effect-free helpers for processing OpenAI responses.

/**
 * Remove markdown code fences from JSON responses.
 * OpenAI often wraps JSON in ```json ... ``` blocks.
 *
 * @param {string} content - Raw response content from OpenAI
 * @returns {string} - Cleaned string with code fences removed
 */
export const cleanJsonResponse = (content) => {
  if (typeof content !== 'string') return '';
  return content.replace(/```json\n?|\n?```/g, '').trim();
};

/**
 * Extract an ACTION block from an AI response.
 * The ACTION block format is:
 *   ---ACTION---
 *   { ... JSON ... }
 *   ---END ACTION---
 *
 * Handles markdown formatting variations (e.g., --- **ACTION** ---).
 *
 * @param {string} response - Full AI response text
 * @returns {{ message: string, action?: object }} - Message text and parsed action (if valid)
 */
export const extractActionBlock = (response) => {
  if (typeof response !== 'string') return { message: '' };

  const actionMatch = response.match(
    /---\s*\*{0,2}ACTION\*{0,2}\s*(?:---?)?\s*([\s\S]*?)\s*---\s*\*{0,2}END\s*ACTION\*{0,2}\s*---?/i
  );

  if (actionMatch) {
    const messageText = response
      .replace(
        /---\s*\*{0,2}ACTION\*{0,2}\s*(?:---?)?[\s\S]*---\s*\*{0,2}END\s*ACTION\*{0,2}\s*---?/i,
        ''
      )
      .trim();

    try {
      const action = JSON.parse(actionMatch[1].trim());
      return { message: messageText, action };
    } catch {
      return { message: response };
    }
  }

  return { message: response };
};

/**
 * Detect whether an OpenAI response was truncated.
 * Two signals: finish_reason === 'length', or JSON not ending with '}'.
 *
 * @param {{ finishReason?: string, content?: string, truncated?: boolean }} result
 *   - finishReason: The finish_reason from the API response
 *   - content: The raw response content (used for JSON end-brace check)
 *   - truncated: Pre-computed flag (if set, takes priority)
 * @returns {{ truncated: boolean, reason: string | null }}
 */
export const detectTruncation = (result) => {
  if (!result || typeof result !== 'object') {
    return { truncated: false, reason: null };
  }

  if (result.truncated === true || result.finishReason === 'length') {
    return { truncated: true, reason: 'Response hit token limit (finish_reason: length)' };
  }

  if (typeof result.content === 'string') {
    const cleaned = cleanJsonResponse(result.content);
    if (cleaned.length > 0 && !cleaned.endsWith('}') && !cleaned.endsWith(']')) {
      return { truncated: true, reason: 'JSON appears malformed (does not end with } or ])' };
    }
  }

  return { truncated: false, reason: null };
};

/**
 * Validate that a parsed estimate result has the expected number of line items.
 * Compares sourceLineItemCount (self-reported by AI) against actual lineItems array length.
 *
 * @param {object} parsed - Parsed estimate object with lineItems array and optional sourceLineItemCount
 * @throws {Error} If sourceLineItemCount exists and actual count is less than reported
 * @returns {{ valid: boolean, reportedCount: number | undefined, actualCount: number }}
 */
export const validateParseResult = (parsed) => {
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('validateParseResult: parsed input must be an object');
  }

  const reportedCount = parsed.sourceLineItemCount;
  const actualCount = parsed.lineItems?.length || 0;

  if (reportedCount && actualCount < reportedCount) {
    throw new Error(
      `PARSING INCOMPLETE: AI reported ${reportedCount} line items in document but only ${actualCount} were captured. ` +
      `${reportedCount - actualCount} items are MISSING.`
    );
  }

  return { valid: true, reportedCount, actualCount };
};

/**
 * Estimate token count from a messages array.
 * Uses the ~4 characters per token heuristic for English text.
 * Handles both string content and multimodal content arrays.
 *
 * @param {Array<{ role: string, content: string | Array<{ type: string, text?: string }> }>} messages
 * @returns {{ totalChars: number, estimatedTokens: number }}
 */
export const estimateTokens = (messages) => {
  if (!Array.isArray(messages)) {
    return { totalChars: 0, estimatedTokens: 0 };
  }

  const totalChars = messages.reduce((sum, m) => {
    if (typeof m.content === 'string') return sum + m.content.length;
    if (Array.isArray(m.content)) {
      return sum + m.content.reduce((s, c) => s + (c.text?.length || 0), 0);
    }
    return sum;
  }, 0);

  const estimatedTokens = Math.ceil(totalChars / 4);

  return { totalChars, estimatedTokens };
};
